import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createExcelWithTextColumns,
  downloadExcel,
  getPermissionDeniedMessage,
  getWriteErrorMessage,
  OUTCOME_UNKNOWN_MESSAGE,
  parseExcelWorkbook,
  GridConfigMenu,
  PamGrid,
  shouldReloadAfterWriteError,
  usePermission,
  useGridConfigs,
  VERSION_CONFLICT_MESSAGE,
  withLoadedModifiedDate,
} from '@1823-partners/core';
import api, {
  fetchRatings,
  fetchRatingsHistory,
  fetchRatingsInputs,
  saveRatings,
} from '../api';
import {
  PERCENT_FIELDS,
  EDITABLE_FIELDS,
  NUMERIC_FIELDS,
  RATING_CHOICES,
  WORKSHEET_COLUMNS,
  editableRecord,
  filterRatings,
  formatRatingValue,
  validateRatings,
} from '../utils/ratings';
import { isStaleWrite, rebaseEdit } from '../utils/modifiedDates';
import RatingsTextEditor from './RatingsTextEditor';
import { RATINGS_UPLOAD_COLUMNS, sizeRatingsColumns, validateRatingsUpload } from '../utils/ratingsUpload';

const DEFAULT_COL_DEF = {
  sortable: true,
  filter: true,
  resizable: true,
  width: 180,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellDataType: false,
};
const EXCEL_STYLES = [
  { id: 'ratings-percent', numberFormat: { format: '0.0000%' } },
  { id: 'ratings-number', numberFormat: { format: '#,##0.######' } },
];
const FILTER_FIELDS = [
  ['TEAM', 'Team'],
  ['ANALYST', 'Analyst'],
  ['ASSET_LVL_2', 'Asset Class — Level 2'],
];
const sourceTooltip = (field) => {
  if (field === 'ANALYST_ABBREVIATION') return 'Current analyst abbreviation maintained on the Input tab.';
  if (EDITABLE_FIELDS.includes(field)) return 'Editable assignment. Save to record a new version.';
  if (['MODIFIED_BY', 'MODIFIED_DATE'].includes(field)) return 'Saved assignment audit value.';
  return `Clearwater PSTN_DAILY_SEC ${field === 'CUSIP' ? 'ID' : field}, used directly from one security row. Duplicate rows are not summed or weighted.${PERCENT_FIELDS.includes(field) ? ' Source units are percentage points.' : ''}`;
};
const errorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  'Request failed.';

export default function RatingsGrid({ inputRevision = 0 }) {
  const [rows, setRows] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [asOf, setAsOf] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const asOfRef = useRef('');
  const [suggestions, setSuggestions] = useState({});
  const [analystAbbreviations, setAnalystAbbreviations] = useState(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const [saveReview, setSaveReview] = useState(null);
  const [filters, setFilters] = useState({ TEAM: '', ANALYST: '', ASSET_LVL_2: '' });
  const [cusipSearch, setCusipSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [historyBusy, setHistoryBusy] = useState(false);
  const uploadInput = useRef(null);
  const pending = useRef(new Map());
  const baseline = useRef(new Map());
  const loadedRows = useRef(new Map());
  const gridRef = useRef(null);
  const busyRef = useRef(false);
  const historyRequest = useRef(0);
  const mounted = useRef(true);
  const gridConfigs = useGridConfigs(api, {
    app: 'secmaster',
    gridId: 'secmaster-ratings',
    gridApiRef: gridRef,
  });
  // Read-only until the permission center confirms the grant.
  const canWrite = usePermission('secmaster.securities:write') && !asOf;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      historyRequest.current += 1;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchRatingsInputs()
      .then(({ data }) => {
        if (active) {
          setSuggestions(data.options);
          setAnalystAbbreviations(new Map(
            (data.entries || [])
              .filter((entry) => entry.KIND === 'ANALYST')
              .map((entry) => [entry.VALUE, entry.ABBREVIATION || ''])
          ));
        }
      })
      .catch((failure) => {
        if (active) setError(`Input lists could not be loaded: ${errorMessage(failure)}`);
      });
    return () => {
      active = false;
    };
  }, [inputRevision]);

  // keepPending re-applies the fields the user changed over the fresh rows
  // (after a version conflict) so they can review and save again.
  const load = useCallback(async (selectedDate = asOfRef.current, keepPending = false) => {
    const response = await fetchRatings(selectedDate || undefined);
    if (!mounted.current) return;
    const data = response.data;
    const before = baseline.current;
    baseline.current = new Map(data.entries.map((row) => [row.CUSIP, editableRecord(row)]));
    loadedRows.current = new Map(data.entries.map((row) => [row.CUSIP, row]));
    if (keepPending) {
      pending.current.forEach((edit, cusip) => {
        const fresh = baseline.current.get(cusip);
        const rebased = rebaseEdit(edit, before.get(cusip), fresh);
        if (JSON.stringify(rebased) === JSON.stringify(fresh)) pending.current.delete(cusip);
        else pending.current.set(cusip, rebased);
      });
      setPendingCount(pending.current.size);
    }
    setRows(
      keepPending
        ? data.entries.map((row) => ({ ...row, ...pending.current.get(row.CUSIP) }))
        : data.entries
    );
    asOfRef.current = selectedDate;
    setAsOf(selectedDate);
    setAvailableDates(data.available_dates || []);
    setSelectedId(null);
    setSnapshot(data.holdings_date);
  }, []);

  const refresh = useCallback(
    async (selectedDate = asOfRef.current, discardConfirmed = false) => {
      gridRef.current?.api?.stopEditing();
      if (busyRef.current) return;
      if (pending.current.size && !discardConfirmed) {
        setConfirmation({ action: 'refresh', selectedDate });
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setError('');
      setNotice('');
      try {
        await load(selectedDate);
        pending.current.clear();
        setPendingCount(0);
      } catch (failure) {
        setError(errorMessage(failure));
      } finally {
        busyRef.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [load]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    const beforeUnload = (event) => {
      if (pending.current.size) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  const onCellValueChanged = useCallback(
    ({ data, colDef, api: gridApi, oldValue }) => {
      if (!canWrite || busyRef.current || !EDITABLE_FIELDS.includes(colDef.field)) {
        data[colDef.field] = oldValue;
        gridApi?.refreshCells();
        return;
      }
      const record = editableRecord({
        ...data,
        ...pending.current.get(data.CUSIP),
        [colDef.field]: data[colDef.field],
      });
      if (JSON.stringify(record) === JSON.stringify(baseline.current.get(data.CUSIP)))
        pending.current.delete(data.CUSIP);
      else pending.current.set(data.CUSIP, record);
      setPendingCount(pending.current.size);
      setRows((current) =>
        current.map((row) => (row.CUSIP === data.CUSIP ? { ...row, ...record } : row))
      );
      gridApi?.refreshCells();
    },
    [canWrite]
  );

  const downloadEditable = async () => {
    gridRef.current?.api?.stopEditing();
    if (!canWrite || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      // Export what the grid shows, including column filters that `filtered`
      // does not know about. Upload only touches the CUSIPs in the file.
      const shown = new Set();
      gridRef.current?.api?.forEachNodeAfterFilter(({ data }) => shown.add(data.CUSIP));
      // stopEditing may have just committed a Team/Analyst edit that the grid
      // has not re-filtered yet, so re-check the toolbar filters on pending values.
      const current = rows.map((row) => ({ ...row, ...pending.current.get(row.CUSIP) }));
      // Assignments are keyed by CUSIP even if a source response has repeated rows.
      const data = [...new Map(filterRatings(current, filters)
        .filter((row) => shown.has(row.CUSIP))
        .map((row) => [row.CUSIP, editableRecord(row)])).values()];
      if (!data.length) throw new Error('No securities match the current filters.');
      const team = filters.TEAM === '__unassigned__' ? 'Unassigned' : filters.TEAM;
      const blob = await createExcelWithTextColumns({
        data,
        columns: RATINGS_UPLOAD_COLUMNS.map((field) => ({ field, headerName: field })),
        textColumns: RATINGS_UPLOAD_COLUMNS,
        sheetName: 'Editable Ratings',
      });
      const { workbook } = await parseExcelWorkbook(blob);
      sizeRatingsColumns(workbook, data);
      // The shared exporter owns cell/text formatting; add workbook layout here.
      const { write } = await import('xlsx');
      const formatted = write(workbook, { bookType: 'xlsx', type: 'array' });
      downloadExcel(formatted, `Editable Ratings ${team ? `${team} ` : ''}${snapshot || 'latest'}.xlsx`);
    } catch (failure) {
      if (mounted.current) setError(`Download failed: ${errorMessage(failure)}`);
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const uploadEditable = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canWrite || busyRef.current) return;
    gridRef.current?.api?.stopEditing();
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (!/\.xlsx?$/i.test(file.name)) throw new Error('Choose an .xlsx or .xls workbook.');
      const { sheetsData, sheetNames } = await parseExcelWorkbook(file);
      if (!mounted.current) return;
      const records = validateRatingsUpload(sheetsData[sheetNames[0]], rows, suggestions, baseline.current);
      const updates = new Map(records.map((record) => [record.CUSIP, record]));
      const nextPending = new Map(pending.current);
      const nextRows = rows.map((row) => {
        const record = updates.get(row.CUSIP);
        if (!record) return { ...row, ...nextPending.get(row.CUSIP) };
        if (JSON.stringify(record) === JSON.stringify(baseline.current.get(row.CUSIP)))
          nextPending.delete(row.CUSIP);
        else nextPending.set(row.CUSIP, record);
        return { ...row, ...record };
      });
      if (nextPending.size > 10000) throw new Error('A save supports up to 10,000 changed securities. Upload fewer changes.');
      pending.current = nextPending;
      setRows(nextRows);
      setPendingCount(nextPending.size);
      setNotice(`Uploaded ${records.length} securities; ${nextPending.size} unsaved record(s). Use Save to write changes to Snowflake, or Discard changes to undo them.`);
    } catch (failure) {
      if (mounted.current) setError(`Upload failed; existing edits are retained. ${errorMessage(failure)}`);
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const requestDiscard = () => {
    gridRef.current?.api?.stopEditing();
    if (busyRef.current || !pending.current.size) return;
    setConfirmation({ action: 'discard' });
  };

  const confirmDiscard = () => {
    const requested = confirmation;
    setConfirmation(null);
    if (!requested || busyRef.current) return;
    if (requested.action === 'refresh') {
      refresh(requested.selectedDate, true);
      return;
    }
    gridRef.current?.api?.stopEditing(true);
    pending.current.clear();
    setPendingCount(0);
    setRows((current) => current.map((row) => ({ ...row, ...baseline.current.get(row.CUSIP) })));
    setError('');
    setNotice('Unsaved changes discarded.');
    gridRef.current?.api?.refreshCells({ force: true });
  };

  const requestSave = () => {
    gridRef.current?.api?.stopEditing();
    if (!canWrite || busyRef.current || !pending.current.size) return;
    const records = Array.from(pending.current.values(), (record) => ({ ...record }));
    const errors = validateRatings(records);
    if (errors.length) {
      setError(errors.join('\n'));
      return;
    }
    const deltas = records.flatMap((record) => EDITABLE_FIELDS.flatMap((field) => {
      const before = baseline.current.get(record.CUSIP)?.[field] ?? null;
      const after = record[field] ?? null;
      return before === after ? [] : [{
        ID: `${record.CUSIP}:${field}`, CUSIP: record.CUSIP,
        FIELD: field === 'RATING' ? 'Rating' : field === 'TEAM' ? 'Team' : 'Analyst',
        BEFORE: before, AFTER: after,
      }];
    }));
    setError('');
    setSaveReview({ records, deltas });
  };

  const save = async () => {
    if (!canWrite || busyRef.current || !saveReview) return;
    const records = saveReview.records;
    setSaveReview(null);
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      // The route skips unchanged records; each carries the MODIFIED_DATE it was
      // loaded with, unchanged (null for a CUSIP never saved).
      const response = await saveRatings(
        records.map((record) => withLoadedModifiedDate(record, loadedRows.current.get(record.CUSIP)))
      );
      records.forEach((record) => baseline.current.set(record.CUSIP, { ...record }));
      pending.current.clear();
      setPendingCount(0);
      setNotice(`Saved ${response.data.saved} ratings record(s).`);
      try {
        await load();
      } catch (failure) {
        setError(
          `Save succeeded, but reload failed: ${errorMessage(failure)}. Refresh to retrieve audit values.`
        );
      }
    } catch (failure) {
      const permissionDenied = getPermissionDeniedMessage(failure);
      if (permissionDenied) {
        setError(`${permissionDenied} Your edits are retained.`);
      } else if (isStaleWrite(failure)) {
        // Reload the latest rows; the edits stay pending for an explicit re-save.
        setError(`${getWriteErrorMessage(failure, VERSION_CONFLICT_MESSAGE)} Your edits are retained; review them and save again.`);
        try {
          await load(undefined, true);
        } catch (reloadFailure) {
          if (mounted.current) setError(`Reload after conflict failed: ${errorMessage(reloadFailure)}`);
        }
      } else if (shouldReloadAfterWriteError(failure)) {
        // The save may or may not have committed (502/503/504, no response):
        // reload so the screen shows what was stored. load(undefined, true) keeps
        // every edit that the reloaded rows do not already contain.
        setError(`Save outcome unknown. ${getWriteErrorMessage(failure, OUTCOME_UNKNOWN_MESSAGE)} The latest ratings were reloaded and your unsaved edits are retained; review them before saving again.`);
        try {
          await load(undefined, true);
        } catch (reloadFailure) {
          if (mounted.current) {
            setError(`Save outcome unknown, and the reload failed: ${errorMessage(reloadFailure)}. Your edits are retained; refresh to check before saving again.`);
          }
        }
      } else {
        setError(`Save failed; your edits are retained. ${getWriteErrorMessage(failure, failure?.message || 'Request failed.')}`);
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const openHistory = useCallback(async (cusip) => {
    if (!cusip) return;
    setSelectedId(cusip);
    const requestId = ++historyRequest.current;
    setHistory({ cusip, saved_versions: [] });
    setHistoryError('');
    setHistoryBusy(true);
    try {
      const response = await fetchRatingsHistory(cusip);
      if (mounted.current && requestId === historyRequest.current)
        setHistory({ cusip, saved_versions: response.data });
    } catch (failure) {
      if (mounted.current && requestId === historyRequest.current)
        setHistoryError(errorMessage(failure));
    } finally {
      if (mounted.current && requestId === historyRequest.current) setHistoryBusy(false);
    }
  }, []);

  const columnDefs = useMemo(
    () =>
      WORKSHEET_COLUMNS.map(([field, headerName]) => {
        const editable = EDITABLE_FIELDS.includes(field);
        const numeric = NUMERIC_FIELDS.includes(field);
        const percent = PERCENT_FIELDS.includes(field);
        const assignment = ['TEAM', 'ANALYST'].includes(field);
        return {
          field,
          headerName,
          valueGetter: field === 'ANALYST_ABBREVIATION'
            ? ({ data }) => analystAbbreviations.get(data?.ANALYST) || ''
            : undefined,
          editable: () => Boolean(canWrite) && !busyRef.current && editable,
          headerTooltip: sourceTooltip(field),
          cellClass: [
            editable ? 'ratings-editable' : 'ratings-readonly',
            percent
              ? 'ratings-percent'
              : numeric
                ? 'ratings-number'
                : 'ratings-text',
          ],
          cellClassRules: {
            'ratings-changed': ({ data }) => pending.current.has(data.CUSIP) && editable,
          },
          pinned: field === 'CUSIP' ? 'left' : undefined,
          cellRenderer:
            field === 'CUSIP'
              ? ({ value }) => (
                  <Button
                    size="small"
                    sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      openHistory(value);
                    }}
                  >
                    {value}
                  </Button>
                )
              : undefined,
          valueFormatter: ({ value }) => formatRatingValue(field, value),
          valueParser: ({ newValue }) =>
            typeof newValue === 'string' ? newValue.trim() || null : newValue,
          cellEditor:
            field === 'RATING'
              ? 'agSelectCellEditor'
              : assignment
                ? RatingsTextEditor
                : 'agTextCellEditor',
          cellEditorParams: ({ data }) =>
            field === 'RATING'
              ? { values: RATING_CHOICES }
              : {
                  values: [
                    ...new Set([
                      '',
                      ...(suggestions[field] || []),
                      ...(data?.[field] ? [data[field]] : []),
                    ]),
                  ],
                },
          filter: numeric ? 'agNumberColumnFilter' : 'agTextColumnFilter',
          filterValueGetter: numeric
            ? ({ data }) => (data[field] == null ? null : Number(data[field]))
            : undefined,
          comparator: numeric
            ? (a, b) => (a == null ? (b == null ? 0 : -1) : b == null ? 1 : Number(a) - Number(b))
            : undefined,
        };
      }),
    [canWrite, suggestions, analystAbbreviations, openHistory]
  );

  const detailColumns = useMemo(
    () =>
      columnDefs.map((col) => ({
        ...col,
        editable: false,
        pinned: undefined,
        cellRenderer: undefined,
        cellClassRules: undefined,
      })),
    [columnDefs]
  );

  const filtered = useMemo(() => {
    const query = cusipSearch.trim().toUpperCase();
    return filterRatings(rows, filters).filter((row) =>
      String(row.CUSIP ?? '').toUpperCase().includes(query)
    );
  }, [rows, filters, cusipSearch]);
  const exportExcel = () => {
    gridRef.current?.api?.stopEditing();
    gridRef.current?.api?.exportDataAsExcel({
      fileName: `Security Level ${snapshot || 'undated'}.xlsx`,
      sheetName: 'Security Level',
      processCellCallback: ({ column, value }) => {
        const field = column.getColId();
        if (value == null || value === '') return '';
        if (NUMERIC_FIELDS.includes(field)) {
          const number = Number(value);
          if (!Number.isFinite(number)) return String(value);
          return PERCENT_FIELDS.includes(field) ? number / 100 : number;
        }
        return formatRatingValue(field, value);
      },
    });
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 1, p: 1 }}
    >
      <Typography variant="body2">
        Clearwater holdings: {snapshot || 'not loaded'} · {filtered.length} securities ·{' '}
        {pendingCount} unsaved record(s)
      </Typography>
      {error && (
        <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}
      {notice && <Alert severity="success">{notice}</Alert>}
      {asOf && (
        <Alert severity="info">
          Historical snapshot: read-only. Ratings show the last version saved by the end of {asOf}{' '}
          (UTC). Ratings before their first saved version are blank. Select Latest to edit.
        </Alert>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <TextField
          select
          label="As-of date"
          size="small"
          sx={{ minWidth: 200 }}
          value={asOf || 'latest'}
          disabled={busy}
          onChange={(event) => refresh(event.target.value === 'latest' ? '' : event.target.value)}
        >
          <MenuItem value="latest">Latest</MenuItem>
          {availableDates.map((day) => (
            <MenuItem key={day} value={day}>
              {day}
            </MenuItem>
          ))}
        </TextField>
        {FILTER_FIELDS.map(([field, label]) => (
          <TextField
            key={field}
            select
            label={label}
            size="small"
            sx={{ minWidth: 200 }}
            value={filters[field]}
            onChange={(event) =>
              setFilters((current) => ({ ...current, [field]: event.target.value }))
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="__unassigned__">Unassigned</MenuItem>
            {[
              ...new Set([
                ...(suggestions[field] || []),
                ...rows.map((row) => row[field]).filter(Boolean),
              ]),
            ]
              .sort()
              .map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
          </TextField>
        ))}
        <Button onClick={() => refresh()} disabled={busy}>
          Refresh
        </Button>
        <Button onClick={requestSave} disabled={busy || !canWrite || !rows.length}>
          Save
        </Button>
        <Button onClick={requestDiscard} disabled={busy || !canWrite || !rows.length}>
          Discard changes
        </Button>
        <Button onClick={downloadEditable} disabled={busy || !canWrite || !rows.length}>
          Download Editable Excel
        </Button>
        <Button onClick={() => uploadInput.current?.click()} disabled={busy || !canWrite || !rows.length}>
          Upload Editable Excel
        </Button>
        <input ref={uploadInput} type="file" accept=".xlsx,.xls" hidden
          aria-label="Upload editable ratings workbook" onChange={uploadEditable} />
        <Button onClick={exportExcel} disabled={busy || !rows.length}>
          Export Excel
        </Button>
        <Button onClick={() => openHistory(selectedId)} disabled={!selectedId}>
          History
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="Search CUSIP"
          placeholder="Full or partial CUSIP"
          size="small"
          value={cusipSearch}
          onChange={(event) => setCusipSearch(event.target.value)}
          sx={{ width: 320, maxWidth: '100%' }}
        />
        <Button onClick={() => setCusipSearch('')} disabled={!cusipSearch}>
          Clear search
        </Button>
        <Box sx={{ flex: 1 }} />
        <GridConfigMenu controller={gridConfigs} />
      </Stack>
      <Typography variant="caption">
        Editable Excel includes the securities shown in the grid; filters and CUSIP search apply.
        Upload replaces Rating, Team and Analyst only for CUSIPs in the file; blank cells clear
        assignments.
        Review the grid and choose Save to write to Snowflake.
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          '& .ratings-readonly': { backgroundColor: 'action.hover' },
          '& .ratings-changed': { backgroundColor: 'rgba(255, 165, 0, 0.2)' },
        }}
      >
        <PamGrid
          ref={gridRef}
          rowData={filtered}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          getRowId={(data) => data.ROW_ID ?? data.CUSIP}
          height="100%"
          containerSx={{ height: '100%' }}
          gridContainerSx={{ height: '100%' }}
          excelStyles={EXCEL_STYLES}
          showFooter={false}
          showToolbar={false}
          showExport={false}
          showQuickFilter={false}
          onGridReady={gridConfigs.onGridReady}
          onFilterChanged={gridConfigs.onGridStateChanged}
          onCellValueChanged={onCellValueChanged}
          {...gridConfigs.stashBindings}
          isLoading={busy}
          onRowClicked={({ data }) => setSelectedId(data.CUSIP)}
          suppressLastEmptyLineOnPaste
        />
      </Box>
      <Dialog
        open={Boolean(saveReview)}
        onClose={() => setSaveReview(null)}
        fullWidth maxWidth="md"
        aria-labelledby="ratings-save-title"
      >
        <DialogTitle id="ratings-save-title">Review Ratings Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {saveReview?.records.length || 0} securities · {saveReview?.deltas.length || 0} changed fields.
            Review all pending changes, including securities hidden by filters.
            Confirm Save writes these changes to Snowflake.
          </Typography>
          {saveReview && <PamGrid
            rowData={saveReview.deltas}
            columnDefs={[
              { field: 'CUSIP', headerName: 'CUSIP', width: 160 },
              { field: 'FIELD', headerName: 'Field', width: 120 },
              { field: 'BEFORE', headerName: 'Saved value', flex: 1, minWidth: 180,
                valueFormatter: ({ value }) => value == null ? '(Unassigned)' : value },
              { field: 'AFTER', headerName: 'New value', flex: 1, minWidth: 180,
                valueFormatter: ({ value }) => value == null ? '(Unassigned)' : value },
            ]}
            defaultColDef={{ sortable: true, resizable: true, editable: false }}
            getRowId={(row) => row.ID}
            height="360px" containerSx={{ height: '360px' }} gridContainerSx={{ height: '100%' }}
            showToolbar={false} showFooter={false} showExport={false} showQuickFilter={false}
          />}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setSaveReview(null)}>Keep editing</Button>
          <Button variant="contained" onClick={save} disabled={busy || !canWrite}>
            Confirm Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(confirmation)}
        onClose={() => setConfirmation(null)}
        aria-labelledby="ratings-discard-title"
      >
        <DialogTitle id="ratings-discard-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmation?.action === 'refresh'
              ? 'Your unsaved rating, analyst and team changes will be discarded when the data reloads.'
              : 'Rating, analyst and team values will return to their last saved values.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmation(null)}>
            Keep editing
          </Button>
          <Button color="error" onClick={confirmDiscard}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(history)}
        onClose={() => {
          historyRequest.current += 1;
          setHistory(null);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Rating, analyst and team changes · {history?.cusip}</DialogTitle>
        <DialogContent>
          {historyBusy && <Typography>Loading history…</Typography>}
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {!historyBusy && !historyError && (
            <>
              {history?.saved_versions.length === 0 && <Typography>No saved versions.</Typography>}
              <PamGrid
                rowData={history?.saved_versions || []}
                columnDefs={[
                  'MODIFIED_DATE',
                  'MODIFIED_BY',
                  'RATING',
                  'TEAM',
                  'ANALYST',
                ].map((field) => detailColumns.find((col) => col.field === field))}
                defaultColDef={DEFAULT_COL_DEF}
                height="320px"
                containerSx={{ height: '320px' }}
                gridContainerSx={{ height: '100%' }}
                showFooter={false}
                showToolbar={false}
                showExport={false}
                showQuickFilter={false}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              historyRequest.current += 1;
              setHistory(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
